// Serviço para gerenciar serviços no Supabase
import { supabase } from '../lib/supabase';
import type { Servico } from '../types';

// Interface para serviço no banco de dados
interface ServicoDB {
  id: string;
  codigo: string;
  descricao: string;
  unidade_medida: string;
  valor: number;
  codigo_municipal?: string;
  created_at: string;
  updated_at: string;
}

// Converter serviço do banco para o formato da aplicação
function servicoDBToServico(servicoDB: ServicoDB): Servico {
  return {
    id: servicoDB.id,
    codigo: servicoDB.codigo,
    descricao: servicoDB.descricao,
    unidadeMedida: servicoDB.unidade_medida,
    valor: servicoDB.valor,
    codigoMunicipal: servicoDB.codigo_municipal || undefined,
    createdAt: servicoDB.created_at,
    updatedAt: servicoDB.updated_at,
  };
}

// Converter serviço da aplicação para o formato do banco
function servicoToServicoDB(servico: Partial<Servico>): Partial<ServicoDB> {
  return {
    codigo: servico.codigo,
    descricao: servico.descricao,
    unidade_medida: servico.unidadeMedida,
    valor: servico.valor,
    codigo_municipal: servico.codigoMunicipal || null,
  };
}

// Buscar todos os serviços da empresa atual
export async function getServicos(empresaId: string): Promise<Servico[]> {
  try {
    const { data: servicos, error } = await supabase
      .from('servicos')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[servicosService] Erro ao buscar serviços:', error);
      return [];
    }

    return (servicos || []).map(servicoDBToServico);
  } catch (error) {
    console.error('[servicosService] Erro geral ao buscar serviços:', error);
    return [];
  }
}

// Buscar serviço por ID
export async function getServicoById(id: string): Promise<Servico | null> {
  try {
    const { data: servico, error } = await supabase
      .from('servicos')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    if (!servico) return null;

    return servicoDBToServico(servico);
  } catch (error) {
    console.error('Erro ao buscar serviço:', error);
    throw error;
  }
}

// Criar novo serviço
export async function createServico(
  servico: Omit<Servico, 'id' | 'createdAt' | 'updatedAt'>,
  contaId: string,
  empresaId: string
): Promise<Servico> {
  try {
    const servicoData = servicoToServicoDB(servico);
    
    const { data: novoServico, error } = await supabase
      .from('servicos')
      .insert({
        ...servicoData,
        conta_id: contaId,
        empresa_id: empresaId,
      })
      .select()
      .single();

    if (error) throw error;

    return servicoDBToServico(novoServico);
  } catch (error) {
    console.error('Erro ao criar serviço:', error);
    throw error;
  }
}

// Atualizar serviço
export async function updateServico(id: string, servico: Partial<Servico>): Promise<Servico> {
  try {
    const servicoData = servicoToServicoDB(servico);
    
    const { data: servicoAtualizado, error } = await supabase
      .from('servicos')
      .update(servicoData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return servicoDBToServico(servicoAtualizado);
  } catch (error) {
    console.error('Erro ao atualizar serviço:', error);
    throw error;
  }
}

// Deletar serviço
export async function deleteServico(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('servicos')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Erro ao deletar serviço:', error);
    throw error;
  }
}

// Buscar serviços por termo (para busca global)
export async function searchServicos(term: string, empresaId: string): Promise<Servico[]> {
  try {
    const termLower = term.toLowerCase();
    
    const { data: servicos, error } = await supabase
      .from('servicos')
      .select('*')
      .eq('empresa_id', empresaId)
      .or(`codigo.ilike.%${term}%,descricao.ilike.%${termLower}%`)
      .limit(20);

    if (error) throw error;

    return (servicos || []).map(servicoDBToServico);
  } catch (error) {
    console.error('Erro ao buscar serviços:', error);
    throw error;
  }
}

